import React from 'react';

import {
  Grid,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Skeleton,
} from '@mui/material';

import { MetricsState } from '../../../propTypes';
import { reportPageStyles as styles } from '../styles';
import { CalculatorKPICard } from './';
import SubsectionHeading from '../../../UtilityComponents/SubsectionHeading';
import LoadingCard from 'src/App/Dashboards/Market/Components/Cards/LoadingCard';

const Subsection = ({
  title,
  metrics,
  reportKPIData,
  currency,
  isUserReportOwner,
  handleEditModalOpen,
  hanldeKpiEdit,
  selectedCompsCount,
  sourceTag,
  ltmDateRange,
  isRevealed,
  status,
}: {
  title: string;
  metrics: MetricsState;
  reportKPIData: any;
  currency: string;
  isUserReportOwner: boolean;
  handleEditModalOpen: () => void;
  hanldeKpiEdit: (key: string, value: any) => void;
  selectedCompsCount: number;
  sourceTag?: string;
  ltmDateRange: string;
  isRevealed: boolean;
  status: 'fetching' | 'success' | 'failed' | null;
}) => {
  const visibleKpis = Object.values(metrics).filter(kpi => kpi.visibility);

  if (visibleKpis.length === 0) {
    return null;
  }

  return (
    <>
      <SubsectionHeading
        title={title}
        sourceTag={sourceTag?.replace(' n ', ' ' + selectedCompsCount + ' ')}
      />
      <Grid container direction="row" alignItems="flex-start" justifyContent="flex-start" xs>
        {visibleKpis.map(kpi => (
          <CalculatorKPICard
            key={kpi.config.key}
            kpiKey={kpi.config.key}
            kpiName={kpi.config.displayName}
            value={reportKPIData[kpi.config.key]}
            tooltipConfig={kpi.config.tooltipConfig}
            unitType={kpi.config.unit}
            currency={currency}
            isUserReportOwner={isUserReportOwner}
            isEditable={kpi.config.isEditable}
            handleEditModalOpen={handleEditModalOpen}
            editMode={kpi.config.editMode}
            hanldeKpiEdit={hanldeKpiEdit}
            selectedCompsCount={selectedCompsCount}
            ltmDateRange={ltmDateRange}
            isDisabled={!isRevealed || status == 'fetching'}
          />
        ))}
      </Grid>
    </>
  );
};

const Section = ({
  title,
  subsections,
  reportKPIData,
  currency,
  isUserReportOwner,
  metrics,
  hanldeKpiEdit,
  selectedCompsCount,
  ltmDateRange,
  MapSectionElements,
  isRevealed,
  setIsBookingComDataRevealed,
  status,
  handleOnBookingComDataRevealed,
  matches,
  isExperimental,
  experimentalDisclaimer,
}: {
  title: string;
  subsections: { title: string; key: keyof typeof metrics; sourceTag?: string }[];
  reportKPIData: any;
  currency: string;
  isUserReportOwner: boolean;
  metrics: { [key: string]: MetricsState };
  hanldeKpiEdit: (key: string, value: any) => void;
  selectedCompsCount: number;
  ltmDateRange: string;
  MapSectionElements: React.ReactNode[];
  isRevealed: boolean;
  setIsBookingComDataRevealed: (value: boolean) => void;
  status: 'fetching' | 'success' | 'failed' | null;
  handleOnBookingComDataRevealed: () => void;
  matches: boolean;
  isExperimental?: boolean;
  experimentalDisclaimer?: string;
}) => {
  return (
    <Grid
      container
      alignItems="flex-start"
      justifyContent="center"
      xs
      sx={{ ...styles.paper, ...styles.reportKPISection }}
      gap={2}
    >
      <Grid
        container
        item
        xs={12}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h1">{title}</Typography>
            {isExperimental && (
              <Chip
                label="Experimental"
                size="small"
                color="warning"
                sx={{ fontWeight: 500, fontSize: '0.75rem' }}
              />
            )}
          </Box>
          {isExperimental && experimentalDisclaimer && (
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 560 }}>
              {experimentalDisclaimer}
            </Typography>
          )}
        </Box>
        {status == 'fetching' && <Skeleton variant="text" width={100} height={40} />}
      </Grid>

      <Grid container item xs={12} sx={{ position: 'relative' }} gap={1}>
        {subsections.map(subsection => (
          <Subsection
            key={subsection.key}
            title={subsection.title}
            metrics={metrics[subsection.key]}
            reportKPIData={reportKPIData}
            currency={currency}
            isUserReportOwner={isUserReportOwner}
            handleEditModalOpen={() => {}}
            hanldeKpiEdit={hanldeKpiEdit}
            selectedCompsCount={selectedCompsCount}
            sourceTag={subsection.sourceTag}
            ltmDateRange={ltmDateRange}
            isRevealed={isRevealed}
            status={status}
          />
        ))}

        {!matches && (
          <Grid container item xs={12} justifyContent="space-between">
            {MapSectionElements.length === 1 ? (
              <Grid
                item
                xs={12}
                sx={{
                  filter: !isRevealed || status == 'fetching' ? 'blur(7px)' : 'none',
                }}
              >
                {MapSectionElements[0]}
              </Grid>
            ) : (
              <>
                <Grid
                  item
                  xs={5.9}
                  sx={{ filter: !isRevealed || status == 'fetching' ? 'blur(7px)' : 'none' }}
                >
                  {MapSectionElements[0]}
                </Grid>
                <Grid
                  item
                  xs={5.9}
                  sx={{ filter: !isRevealed || status == 'fetching' ? 'blur(7px)' : 'none' }}
                >
                  {MapSectionElements[1]}
                </Grid>
              </>
            )}
          </Grid>
        )}

        {matches &&
          MapSectionElements.map((element, index) => (
            <Grid
              container
              item
              xs={12}
              key={index}
              sx={{ filter: !isRevealed || status == 'fetching' ? 'blur(10px)' : 'none' }}
            >
              {element}
            </Grid>
          ))}

        {!isRevealed && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              backdropFilter: 'blur(0.1px)',
            }}
          >
            <Button
              variant="outlined"
              sx={{
                ...styles.actionSectionButton,
                width: 'auto',
                margin: '20px auto 0px auto',
                px: '30px',
              }}
              onClick={handleOnBookingComDataRevealed}
              disabled={status == 'failed'}
            >
              {status == 'failed'
                ? 'We couldnt get the data for this market right now'
                : 'Reveal'}
            </Button>
          </Box>
        )}

        {isRevealed && status == 'fetching' && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              backdropFilter: 'blur(0.1px)',
            }}
          >
            <Button
              variant="outlined"
              sx={{
                ...styles.actionSectionButton,
                width: 'auto',
                margin: '20px auto 0px auto',
                px: '30px',
              }}
            >
              <CircularProgress size={20} sx={{ color: '#fff' }} />
            </Button>
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default Section;
